import { useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { GetDrawDownRepaymentScheduleCreditFacilityResponse } from '@iwoca/lapi-client/edge';
import { Button, Link, Modal } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import { addWeeks, addMonths } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import messages from './InlineEarlyRepaymentCalculator.messages';
import styles from './InlineEarlyRepaymentCalculator.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { InformationSVG } from '../../../../svgComponents/informationSVG/InformationSVG';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { buildRepaymentScheduleState } from '../../../modals/repaymentScheduleModal/RepaymentScheduleHelper';
import { RepaymentScheduleModalContent } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import SummaryRow from '../../../ui/summaryRow/SummaryRow';
import { FormalOffer } from '../../MyOffers.types';
import BalanceAfterTopup from '../earlyRepaymentCalculator/balanceAfterTopup/BalanceAfterTopup';
import { differenceBetweenDates } from '../earlyRepaymentCalculator/EarlyRepaymentHelper';
import Fees from '../earlyRepaymentCalculator/fees/Fees';
import InterestOnLoan from '../earlyRepaymentCalculator/interestOnLoan/InterestOnLoan';
import SavingsMessages from '../earlyRepaymentCalculator/savings/Savings.messages';
import savingsStyles from '../earlyRepaymentCalculator/savings/Savings.module.css';

export const InlineEarlyRepaymentCalculator = ({
    drawdownAmount,
    offerId,
    productType,
    loanDuration,
    maxDate,
    variant = 'heading-and-subheading',
    showOnMobile = false,
    infoBoxClassName
}: {
    drawdownAmount: number;
    offerId: string | null;
    loanDuration: number;
    maxDate: string;
    productType: NonNullable<FormalOffer['product_type']>;
    variant?: 'heading-and-subheading' | 'just-subheading';
    showOnMobile?: boolean;
    infoBoxClassName?: string;
}) => {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [activePreset, setActivePreset] = useState(null);
    const [difference, setDifference] = useState(null);
    const [currentDate, setCurrentDate] = useState<string | null>(null);

    const stateKey = useStateKey();

    const inputRef = useRef(null);

    const minDate = new Date().toISOString().slice(0, 10);

    let earlyScheduleSummary: GetDrawDownRepaymentScheduleCreditFacilityResponse['data']['summary'];
    let totalSaving = 0;
    let totalInterest = 0;
    let totalBalance = 0;
    let totalFees = 0;

    const [isModalOpen, setModalOpen] = useState(false);

    const {
        isLoading,
        data: earlyDrawdownRepaymentSchedule,
        isError
    } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            currentDate,
            productType,
            offerId
        ],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${productType}/${offerId}/?amount=${drawdownAmount}&early_final_repayment_date=${currentDate}`
            );
            return response.json();
        },
        enabled: Boolean(currentDate)
    });

    const repaymentScheduleModal = earlyDrawdownRepaymentSchedule
        ? {
              ...buildRepaymentScheduleState(
                  earlyDrawdownRepaymentSchedule.data
              ),
              accountId: null
          }
        : null;

    if (isError) {
        return <FallbackErrorModal />;
    }

    if (earlyDrawdownRepaymentSchedule?.data) {
        earlyScheduleSummary = earlyDrawdownRepaymentSchedule.data.summary;
        totalInterest = earlyScheduleSummary!.total_interest!;
        totalBalance = earlyScheduleSummary!.total_payable!;
        totalFees = earlyScheduleSummary!.total_fees!;
        totalSaving = earlyScheduleSummary!.amount_saved!;
    }

    const handleReset = (inputRef: React.MutableRefObject<any>) => {
        inputRef.current.value = '';
        setShowBreakdown(false);
        setEndDate(null);
        setActivePreset(null);
        setCurrentDate(null);
        setDifference(null);
    };

    const handleDateChange = (date: string, label?: string) => {
        //Used minDate comparison to determine valid date
        const parsedDate = Date.parse(date);
        if (parsedDate < Date.parse(minDate)) {
            setEndDate(date);
        } else {
            setShowBreakdown(true);
            setCurrentDate(date);
            setEndDate(date);
            //@ts-expect-error
            setDifference(differenceBetweenDates(date));
            //amplitude tracking
            tracking.ampli.earlyRepaymentCalculatorUsed({
                early_repayment_date: date,
                preset_button: String(label)
            });
        }
    };

    const renderEarlyBreakdownTitle = () => {
        //@ts-ignore
        const [years, months, days] = difference;

        return years > 0 ? (
            <FormattedMessage
                {...messages.breakdownTitleYears}
                values={{ years, months, days }}
            />
        ) : months > 0 && days > 0 ? (
            <FormattedMessage
                {...messages.breakdownTitleMonthsAndDays}
                values={{ months, days }}
            />
        ) : months > 0 ? (
            <FormattedMessage
                {...messages.breakdownTitleMonths}
                values={{ months }}
            />
        ) : (
            <FormattedMessage
                {...messages.breakdownTitleDays}
                values={{ days }}
            />
        );
    };

    const renderPresets = () => {
        const fourWeeks = addWeeks(new Date(), 4);
        const sixMonths = addMonths(new Date(), 6);
        const nineMonths = addMonths(new Date(), 9);

        const presetDates = [
            {
                key: 'early-repayment-4-weeks-preset',
                label: <FormattedMessage {...messages.fourWeeks} />,
                value: fourWeeks.toISOString().substr(0, 10)
            },
            {
                key: 'early-repayment-6-months-preset',
                label: <FormattedMessage {...messages.sixMonths} />,
                value: sixMonths.toISOString().substr(0, 10)
            },
            {
                key: 'early-repayment-9-months-preset',
                label: <FormattedMessage {...messages.nineMonths} />,
                value: nineMonths.toISOString().substr(0, 10)
            }
        ];

        return (
            <div className={styles.presetButtons}>
                {presetDates.map((preset) => (
                    <Button
                        variant="secondary-alt"
                        className={classnames(styles.preset, {
                            [styles.activePreset]: activePreset === preset.key
                        })}
                        size="small"
                        onClick={() => {
                            //@ts-expect-error
                            setActivePreset(preset.key);
                            handleDateChange(
                                preset.value,
                                preset.label.props.defaultMessage
                            );
                        }}
                        data-tracking-id={preset.key}
                        key={preset.key}
                        data-action-name={preset.key}
                    >
                        {preset.label}
                    </Button>
                ))}
            </div>
        );
    };

    return (
        <>
            <section
                className={classnames(
                    styles.container,
                    showOnMobile ? styles.showOnMobile : null
                )}
                data-testid="desktop-calculator"
            >
                <div className={styles.headerContainer}>
                    <div className={styles.header}>
                        {variant === 'heading-and-subheading' && (
                            <span className={styles.titleLineOne}>
                                <FormattedMessage {...messages.titleLineOne} />
                            </span>
                        )}

                        <span
                            className={
                                variant === 'just-subheading'
                                    ? styles.titleLineOne
                                    : styles.titleLineTwo
                            }
                        >
                            <FormattedMessage {...messages.titleLineTwo} />
                        </span>
                    </div>
                    {showBreakdown && (
                        <Button
                            variant="tertiary"
                            size="small"
                            onClick={() => handleReset(inputRef)}
                        >
                            <FormattedMessage {...messages.clear} />
                        </Button>
                    )}
                </div>

                <div className={styles.datePicker}>
                    <FormattedMessage {...messages.repay} />
                    <input
                        data-testid="early-repayment-date-picker"
                        className={styles.dateInput}
                        type="date"
                        //@ts-expect-error
                        value={endDate}
                        min={minDate} // todays date
                        max={maxDate} // 364 days in future
                        onChange={(e) => {
                            e.persist();
                            setActivePreset(null);
                            handleDateChange(e.target.value);
                        }}
                        data-tracking-id="early-repayment-calendar-input"
                        data-action-name="Early repayment calendar input"
                        ref={inputRef}
                    />
                </div>

                <div className={styles.datePresets}>
                    <div className={styles.orIn}>
                        <FormattedMessage {...messages.orIn} />
                    </div>

                    {renderPresets()}
                </div>

                {showBreakdown && (
                    <div
                        className={classnames(styles.earlyRepaymentBreakdown, {
                            [styles.loading]: isLoading
                        })}
                    >
                        <h3
                            className={styles.breakdownTitle}
                            data-testid="early-repayment-breakdown-title"
                        >
                            {renderEarlyBreakdownTitle()}
                        </h3>

                        <div className={styles.totals}>
                            <InterestOnLoan
                                totalInterest={totalInterest}
                                isLoading={isLoading}
                            />
                            <Fees totalFees={totalFees} isLoading={isLoading} />
                            <BalanceAfterTopup
                                totalBalance={totalBalance}
                                isLoading={isLoading}
                            />
                            <SummaryRow isLoading={isLoading}>
                                <Link
                                    onClick={() => setModalOpen(true)}
                                    data-action-name="Show early repayment schedule"
                                    className={
                                        styles.earlyDrawdownRepaymentScheduleLink
                                    }
                                >
                                    <FormattedMessage
                                        {...messages.seeExampleSchedule}
                                    />
                                </Link>
                                <div
                                    className={classnames(
                                        savingsStyles.savesMe,
                                        {
                                            [savingsStyles.loading]: isLoading
                                        }
                                    )}
                                >
                                    <FormattedMessage
                                        {...SavingsMessages.thisSavesMe}
                                    />
                                    <span data-testid="early-repayment-interest-savings">
                                        <MoneyWithDecimals
                                            value={totalSaving}
                                        />
                                    </span>
                                </div>
                            </SummaryRow>
                        </div>

                        <div
                            className={classnames(
                                styles.info,
                                infoBoxClassName
                            )}
                        >
                            <InformationSVG className={styles.infoSVG} />
                            <FormattedMessage
                                {...messages.info}
                                values={{ months: loanDuration }}
                            />
                        </div>
                    </div>
                )}
            </section>
            <Modal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                closeIcon
            >
                <RepaymentScheduleModalContent
                    repaymentScheduleModal={repaymentScheduleModal!}
                />
            </Modal>
        </>
    );
};
